import React, { useMemo } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import SearchPage from '../SearchPage/SearchPage';
import SearchPageVariation from './SearchPageVariation';
import { parseAmenityFromPath, getFilterParamForAmenity, parseSizeFromPath, getFilterSqftForSize, parseVehicleFromPath } from 'lib/search/parseAmenityFromPath';

const SearchPageSwitcher = (ownProps) => {
	const derivedProps = useMemo(() => {
		// Parse query params from the location (if present)
		const params = ownProps.location
			? new URLSearchParams(ownProps.location.search)
			: new URLSearchParams('');
		const queryParams = ownProps.location ? Object.fromEntries(params) : {};

		// Inject the amenity filter param so checkboxes reflect the active filter.
		// The amenity slug may come from the pathname (clean URL like
		// /Chicago-IL-self-storage/drive-up-storage-units.html) or from the
		// query string (?amenity=drive-up-storage-units). When the user unchecks
		// the filter, the amenity param is also cleared (see handleAmenityToggle),
		// so this won't re-inject after an explicit uncheck.
		const amenitySlug = parseAmenityFromPath(ownProps.location?.pathname) || queryParams.amenity;
		if (amenitySlug) {
			const filterParam = getFilterParamForAmenity(amenitySlug);
			if (filterParam && !queryParams[filterParam]) {
				queryParams[filterParam] = 'true';
			}
			// Ensure amenity is in queryParams so that filter handlers can clear it
			// when the user unchecks the corresponding filter. Without this,
			// handleSearchParamChange spreads searchParams (from Redux) which
			// re-introduces amenity into the URL.
			if (!queryParams.amenity) {
				queryParams.amenity = amenitySlug;
			}
		}

		// Inject sqft filter so the size radio reflects the active SLP page.
		// On SLP URLs like /Chicago-IL-self-storage/5x5-storage-units.html the
		// size is encoded in the path, not the query string, so queryParams.sqft
		// would otherwise be undefined and nothing would be checked.
		if (!queryParams.sqft) {
			const sizeSlug = parseSizeFromPath(ownProps.location?.pathname);
			const filterSqft = sizeSlug && getFilterSqftForSize(sizeSlug);
			if (filterSqft) {
				queryParams.sqft = filterSqft;
			}
		}

		// Inject vehicle filter so checkboxes reflect the active vehicle landing page.
		// On URLs like /Chicago-IL-self-storage/car-storage.html the vehicle type is
		// in the path, not the query string, so queryParams.vehicle would be undefined.
		// vehicleTypes must also be injected because handleVehicleTypeToggle builds
		// currentTypes from vehicleTypes only — without it, clicking a second vehicle
		// type starts from [] and unchecks the path-derived selection.
		if (!queryParams.vehicle && !queryParams.vehicleTypes) {
			const vehicleFromPath = parseVehicleFromPath(ownProps.location?.pathname);
			if (vehicleFromPath) {
				queryParams.vehicle = vehicleFromPath;
				queryParams.vehicleTypes = vehicleFromPath;
				queryParams.searchType = 'vehicle';
			}
		}

		const urlQueryParams = ownProps.location ? Object.fromEntries(params) : {};

		return {
			queryParams,
			urlQueryParams,
		};
	}, [ownProps.location]);

	return <SearchPageVariation {...ownProps} {...derivedProps} />;
};

export default SearchPageSwitcher;
